import React, { useCallback, useMemo } from 'react'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ModalFormId } from 'src/components/ModalForms'
import { StandardButtonSize } from 'src/components/Reusable/Buttons'
import { E2E_IDS } from 'src/Utils/testIds'
import { EditableShowPageTitle } from 'src/components/Reusable/ShowPage/Title/EditableShowPageTitle'
import { Risk, RISK_MATH } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { SubRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useNavigate } from 'react-router-dom'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { FlexColumn, FlexAlign } from 'src/components/Reusable/Flex'
import {
  RiskFields,
  RISK_STATUS_FILTER,
  RiskValue,
  RISK_LABEL_TO_STATUS,
  RISK_STATUS_BG,
} from './risks.constants'
import { RisksMathMenu } from './menus'
import { getRiskMathCalculation, updatedRiskRpcData } from './risks.helpers'
import {
  useCreateOrUpdateRisks,
  useInvalidateRisksCache,
  useActualRiskCalculation,
} from './risks.hooks'
import { RiskStatusOption } from './risks.styles'

export const AddRiskModalBtn = ({
  text,
  handleClick,
}: {
  text: string
  handleClick: () => void
}): JSX.Element => {
  const canAddRisks = useCanPerformAction(CAPABILITY.CAN_ADD_RISKS)
  return (
    <StandardOpenModalButton
      modalId={ModalFormId.ADD_RISK}
      size={StandardButtonSize.SMALL}
      text={text}
      testId={E2E_IDS.ADD_RISK}
      onClick={handleClick}
      isDisabled={!canAddRisks}
      disabledReason="You do not have permission to add risks."
    />
  )
}
/**
 * Helper component to display the Inherent Risk which is based off impact & likelihood
 *
 * The formula essentially takes an average of the two values and returns a RISK_MATH calculation
 *
 * @param impact
 * @param likelihood
 * @returns <RiskMathMenu /> component
 */
export const CalculatedRiskMathChip = ({
  riskId,
  impact,
  likelihood,
  field,
  showValue,
  $height,
}: {
  riskId: string
  impact: RISK_MATH
  likelihood: RISK_MATH
  field: RiskFields
  showValue?: boolean
  $height?: string
}): JSX.Element => (
  <RisksMathMenu
    riskId={riskId}
    field={field}
    initValue={getRiskMathCalculation(impact, likelihood)}
    $height={$height}
    showValue={showValue}
    isReadOnly
  />
)

export const CalculatedActualResidualRisk = ({
  risk,
  field,
  $height,
}: {
  risk: Risk
  field: RiskFields
  $height?: string
}): JSX.Element => {
  const { actualRiskReduction } = useActualRiskCalculation(risk)
  return (
    <RisksMathMenu
      riskId={risk.getId()}
      field={field}
      initValue={actualRiskReduction}
      $height={$height}
      isReadOnly
    />
  )
}

export const RisksShowTitle = ({ risk }: { risk: Risk }): JSX.Element => {
  const createOrUpdateRisks = useCreateOrUpdateRisks(false, false)
  const canUpdateRiskName = useCanPerformAction(CAPABILITY.CAN_UPDATE_RISK_NAME)
  const canUpdateRisksId = useCanPerformAction(CAPABILITY.CAN_UPDATE_RISK_ID)
  const navigate = useNavigate()
  const mutate = useInvalidateRisksCache()
  const riskId = risk.getId()
  const riskName = risk.getName()
  const riskCustomId = risk.getCustomId()

  const onUpdate = async (newName: string, newCustomId?: string) => {
    let updatedRisk: Risk
    updatedRisk = updatedRiskRpcData(risk, RiskFields.name, newName)
    if (newCustomId) {
      updatedRisk = updatedRiskRpcData(
        updatedRisk,
        RiskFields.customId,
        newCustomId,
      )
    }
    await createOrUpdateRisks([updatedRisk])
    if (riskCustomId) {
      navigate(`../../${SubRoutes.SHOW}/${riskId}`)
    }
    await mutate()
  }

  return (
    <EditableShowPageTitle
      key={`${riskCustomId}-${riskName}`}
      name={riskName}
      customId={riskCustomId}
      modelType={MODEL_TYPE.RISK}
      onUpdate={onUpdate}
      isDisabled={!canUpdateRiskName || !canUpdateRisksId}
    />
  )
}

export const StatusDropdown = ({ risk }: { risk: Risk }): JSX.Element => {
  const canUpdateStatus = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISK_STATUS,
    risk.toObject(),
  )
  const createOrUpdateRisks = useCreateOrUpdateRisks(false, false)
  const status = risk.getStatus()
  const initialOption = useMemo(() => {
    return {
      name:
        RISK_STATUS_FILTER.find((option) => option.value === status)?.name ||
        '',
      value: status,
    }
  }, [status])

  const onSelectionChange = async (selected: BasicDropdownItem | null) => {
    if (!selected) return
    const updatedRisk = updatedRiskRpcData(
      risk,
      RiskFields.status,
      selected.value as RiskValue,
    )
    await createOrUpdateRisks([updatedRisk])
  }

  const dropdownItems = RISK_STATUS_FILTER.map(
    (option) =>
      ({
        name: option.name,
        value: option.value,
      } as BasicDropdownItem),
  )

  return (
    <BasicDropdown
      selectedItem={initialOption}
      items={dropdownItems}
      onSelect={onSelectionChange}
      backgroundColor={RISK_STATUS_FILTER[status].color}
      borderColor={RISK_STATUS_FILTER[status].border}
      disabled={!canUpdateStatus}
    />
  )
}

export const StatusMenu = ({ risk }: { risk: Risk }): JSX.Element => {
  const createOrUpdateRisks = useCreateOrUpdateRisks(false, false)
  const mutate = useInvalidateRisksCache()
  const selectStatus = useCallback(
    async (status: RiskValue) => {
      const updatedRisk = updatedRiskRpcData(risk, RiskFields.status, status)
      await createOrUpdateRisks([updatedRisk])
      mutate()
    },
    [risk, createOrUpdateRisks, mutate],
  )
  return (
    <FlexColumn
      justify={FlexAlign.FLEX_START}
      align={FlexAlign.FLEX_START}
      gap={8}
    >
      {Object.keys(RISK_LABEL_TO_STATUS).map((label) => {
        const value = RISK_LABEL_TO_STATUS[label]
        return (
          <RiskStatusOption
            key={label}
            background={RISK_STATUS_BG[value]}
            onClick={() => selectStatus(value)}
          >
            {label}
          </RiskStatusOption>
        )
      })}
    </FlexColumn>
  )
}
